import React, { useState } from 'react'
import { ActionButton } from '@emerald-works-nova/components'
import { useEvent } from '@emerald-works/react-event-bus-client'
import { Box, Collapse, Divider, Grid, List, ListItem, ListItemText, Switch, Typography, useTheme } from '@mui/material'
import { useDispatch } from 'react-redux'
import { BannerStatic } from '../../../components'
import { TenantEditSlice } from '../../../reducers'
import { useStyles } from './style'
import { useFeatureFormContext } from './feature-form-context'
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material'

const LpqSettings = () => {
  const classes = useStyles()
  const { spacing } = useTheme()
  const dispatch = useDispatch()
  const { tenant } = useFeatureFormContext()
  const [isLoading, setIsLoading] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(false)

  const [value, setValue] = useState({
    enabled: tenant?.features?.lpq?.enabled !== false,
    showModalPopup: tenant?.features?.lpq?.showModalPopup !== false,
    isOptional: tenant?.features?.lpq?.isOptional !== false
  })

  const handleSwitch = () => {
    setValue(current => ({ ...current, enabled: !current.enabled }))
  }

  const handleModalPopupSwitch = () => {
    setValue(current => {
      const showModalPopup = !current.showModalPopup
      // A mandatory questionnaire (isOptional === false) is only enforceable when
      // the modal popup prompts the user. Turning the popup off forces the
      // questionnaire back to optional so users can never be trapped in a
      // mandatory LPQ they were never prompted to complete.
      return {
        ...current,
        showModalPopup,
        isOptional: showModalPopup ? current.isOptional : true
      }
    })
  }

  const handleOptionalSwitch = () => {
    setValue(current => ({ ...current, isOptional: !current.isOptional }))
  }

  const handleLpqSettings = () => setIsCollapsed(collapsed => !collapsed)

  const [tenantUpdate] = useEvent([
    {
      eventName: 'tenantUpdate',
      onSuccess: (tenant) => {
        BannerStatic.show({ label: 'Learning Personalization Questionnaire configuration updated' })
        setIsLoading(false)
        dispatch(TenantEditSlice.actions.updateTenantFeatures(tenant.features))
      },
      onError: () => {
        BannerStatic.show({
          label: 'There was an error saving the Learning Personalization Questionnaire feature configuration',
          isError: true
        })
        setIsLoading(false)
      }
    }
  ])

  const handleSave = () => {
    // Enforce the invariant at the persistence boundary: mandatory
    // (isOptional === false) requires showModalPopup === true.
    const lpq = { ...value, isOptional: value.showModalPopup ? value.isOptional : true }
    tenantUpdate.trigger({
      pk: tenant.pk,
      features: {
        ...tenant.features,
        lpq
      }
    })
    setIsLoading(true)
  }

  return (
    <Grid container className={classes.featContainer}>
      <Grid item xs={12}>
        <Box
          border={1}
          borderRadius='undefinedpx'
          borderColor='#E0E0E0'
          display='flex'
          style={{
            justifyContent: 'space-between',
            flexDirection: 'column',
            padding: spacing(1, 4)
          }}
        >
          <Box display='flex' justifyContent='space-between' alignItems='flex-start'>
            <Typography variant='body2' sx={{ mt: spacing(3.5) }}>
              Learning Personalization Questionnaire
            </Typography>
            <Box display='flex' justifyContent='flex-end' alignItems='center' padding={spacing(1)}>
              {value.enabled && !isLoading && (
                <>
                  <Box sx={{ ':hover': { cursor: 'pointer' } }}>
                    <List component='nav' aria-label='LPQ settings'>
                      <ListItem
                        onClick={handleLpqSettings}
                        aria-haspopup='true'
                        aria-controls='lpq-settings-menu'
                        aria-label='LPQ Settings'
                      >
                        <ListItemText disableTypography sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography variant='body2'>LPQ Settings</Typography>
                          {isCollapsed ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </ListItemText>
                      </ListItem>
                    </List>
                  </Box>
                  <Divider variant='middle' flexItem orientation='vertical' sx={{ mx: spacing(2), borderColor: 'rgba(0, 0, 0, 0.38)' }} />
                </>
              )}
              <Switch
                color='primary'
                checked={value.enabled}
                onChange={handleSwitch}
                disabled={isLoading}
                data-test='lpq-feature-toggle'
              />
            </Box>
          </Box>

          <Collapse in={isCollapsed}>
            {value.enabled && !isLoading && (
              <Box display='flex' flexDirection='column' gap={2} mb={spacing(1)}>
                <Box display='flex' justifyContent='space-between' alignItems='center'>
                  <Box width='50%'>
                    <Typography variant='body1' sx={{ mt: spacing(1), fontWeight: 500 }}>
                      Show Modal Popup
                    </Typography>
                    <Typography variant='body2' sx={{ mt: spacing(1) }}>
                      Enable the modal popup to prompt users to complete the Learning Personalization Questionnaire.
                    </Typography>
                  </Box>
                  <Switch
                    color='primary'
                    checked={value.showModalPopup}
                    onChange={handleModalPopupSwitch}
                    disabled={!value.enabled}
                    data-test='lpq-show-modal-popup-toggle'
                  />
                </Box>
                <Box display='flex' justifyContent='space-between' alignItems='center'>
                  <Box width='50%'>
                    <Typography variant='body1' sx={{ mt: spacing(1), fontWeight: 500 }}>
                      Optional Questionnaire
                    </Typography>
                    <Typography variant='body2' sx={{ mt: spacing(1) }}>
                      When on, users can skip the questionnaire and complete it later. When off, users must complete it and cannot skip or dismiss it.
                    </Typography>
                    {!value.showModalPopup && (
                      <Typography variant='body2' color='textSecondary' sx={{ mt: spacing(1), fontStyle: 'italic' }}>
                        Turn on "Show Modal Popup" to make the questionnaire mandatory.
                      </Typography>
                    )}
                  </Box>
                  <Switch
                    color='primary'
                    checked={value.showModalPopup ? value.isOptional : true}
                    onChange={handleOptionalSwitch}
                    disabled={!value.enabled || !value.showModalPopup}
                    data-test='lpq-optional-toggle'
                  />
                </Box>
              </Box>
            )}
          </Collapse>

          <Divider style={{ marginBottom: spacing(1) }} />

          <Box display='flex' justifyContent='flex-end' mt={1} mb={1}>
            <ActionButton
              onClick={handleSave}
              disabled={isLoading}
              variant='primary'
              dataTest='lpq-save-button'
              style={{ height: 'min-content' }}
            > Save
            </ActionButton>
          </Box>
        </Box>
      </Grid>
    </Grid>
  )
}

export default LpqSettings
